import type { QueryClient } from '@tanstack/react-query'

import { apiClient, getData } from '@/api/client'
import { marketEndpoints } from '@/api/endpointCache'
import type { components } from '@/api/schema'
import { rupeesToArba, rupeesToCrore, sectorSlug } from '@/features/market/api/marketMappers'
import { add, money, ZERO } from '@/lib/money'
import {
  sectorStatsSchema,
  sectorsOverviewSchema,
  type SectorStats,
  type SectorView,
  type SectorsOverview,
} from '@/schemas/sectors'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/** Exported so it is testable on a fixture without a request. */
export const mapSectorStats = (wire: Wire<'SectorStats'>) => ({
  sector: wire.sector,
  count: wire.count,
  metrics: wire.metrics.map((metric) => ({
    metric: metric.metric,
    label: metric.label,
    median: metric.median,
    average: metric.average,
    low: metric.low,
    high: metric.high,
    reporting: metric.reporting,
  })),
})

/**
 * A feed row as it may really arrive: the spec says `value`, `change_percent`
 * and `breadth` are always there, but one sector with a hole must cost that
 * row only. The schema then rejects the row and `lenientList` drops it,
 * instead of a property read throwing before the schema runs.
 */
type LooseSectorRow = { [K in keyof Wire<'SectorRow'>]?: Wire<'SectorRow'>[K] | null }
type LooseSlice = {
  [K in keyof Wire<'SectorTurnoverSlice'>]?: Wire<'SectorTurnoverSlice'>[K] | null
}

export const sectorsService = {
  overview: async (view: SectorView, client?: QueryClient): Promise<SectorsOverview> => {
    const api = marketEndpoints(client)
    const [sectors, distribution] = await Promise.all([api.sectors(view), api.sectorTurnover()])
    const sectorRows: LooseSectorRow[] = sectors.rows
    const sliceRows: LooseSlice[] = distribution.rows
    const totalTurnover = sliceRows.reduce(
      (total, row) => (row.turnover == null ? total : add(total, money(row.turnover))),
      ZERO,
    )

    return sectorsOverviewSchema.parse({
      heat: sectorRows.map((row) => ({
        id: sectorSlug(row.key ?? ''),
        name: row.name,
        percentChange: row.change_percent,
        turnoverArba: rupeesToArba(row.turnover ?? null),
      })),
      caption: 'Width is turnover · colour is today’s change',
      share: {
        totalArba: rupeesToArba(totalTurnover),
        count: sliceRows.length,
        slices: sliceRows.map((row) => ({
          id: sectorSlug(row.key ?? ''),
          name: row.name,
          percent: row.share_percent == null ? Number.NaN : Number(row.share_percent),
        })),
        caption: 'Share of total sector turnover',
      },
      cards: sectorRows.map((row) => ({
        id: sectorSlug(row.key ?? ''),
        name: row.name,
        percentChange: row.change_percent,
        index: row.value,
        volumeKitta: row.volume ?? null,
        turnoverCr: rupeesToCrore(row.turnover ?? null),
        rose: row.breadth?.advances,
        fell: row.breadth?.declines,
      })),
      mitraSays: 'Sector breadth shows whether a move is broad or led by only a few companies.',
      explain: ['What is sector breadth?', 'How is turnover share calculated?'],
    })
  },

  /**
   * One sector's median, average, low and high for fourteen key metrics.
   * `symbol` is the sector's SYMBOL (a company overview's
   * `profile.sectorSymbol`), not its display name or the kebab id the Market
   * tables filter on. Public: no session needed.
   */
  stats: async (symbol: string): Promise<SectorStats> => {
    const data = await getData<Wire<'SectorStats'>>(() =>
      apiClient.GET('/api/v5/market/sectors/{symbol}/stats/', { params: { path: { symbol } } }),
    )
    return sectorStatsSchema.parse(mapSectorStats(data))
  },
}
